import clsx from "clsx";
import { FunctionComponent } from "react";
import Switch from "react-bootstrap/Switch";
import { DropdownFilterDataProps } from "../../../../../components/DropdownFilter";
import cardStyles from "../../../../../components/subheader/card/card.module.scss";
import { Tooltip } from "../../../../../components/tooltip";
import { enums, settings } from "../../../../../shim/jsbridge";
import {
  getMenuItemForPlatform,
  isLocaleAvailableForPlatform
} from "../../../../../utils";
import { AnalysisTypeInfo } from "../../../../header/hooks/usePrepareReanalyzeData";
import GeolocationMenuItem, {
  GEOLOCATION_TO_LABEL
} from "../../geolocationmenuitem/GeolocationMenuItem";
import ReanalyzeCardContainer from "../reanalyze-card-container/ReanalyzeCardContainer";
import ReanalyzeDropdown from "../reanalyze-dropdown/ReanalyzeDropdown";
import ReanalyzeFormInput, {
  LAYOUT_TYPE
} from "../reanalyze-form-input/ReanalyzeFormInput";
import {
  DEFAULT_MENU_STYLES,
  RlcsParams
} from "../reanalyze-form/ReanalyzeForm";

interface RlcsConfigurationInputProps {
  rlcsSelected: boolean;
  analysisInfo?: AnalysisTypeInfo;
  rlcsParams: RlcsParams;
  disabled?: boolean;
  interactiveAnalysisDisabled?: boolean;
  toggleRlcsSelected: () => void;
  toggleRlcsInteractiveSelected: (updatedPlatform: string) => void;
  onChangeRlcsPlatform: (value: DropdownFilterDataProps<string>) => void;
  onChangeRlcsLocale: (value: DropdownFilterDataProps<string>) => void;
  onChangeRlcsGeolocation: (value: DropdownFilterDataProps<string>) => void;
}

const PLATFORMS_SUPPORTED_BY_INTERACTIVE_ANALYSIS = [
  enums.platforms.WINDOWS_10,
  enums.platforms.WINDOWS_11
];

const RlcsConfigurationInput: FunctionComponent<
  RlcsConfigurationInputProps
> = ({
  rlcsSelected,
  rlcsParams,
  analysisInfo,
  disabled,
  interactiveAnalysisDisabled = false,
  toggleRlcsSelected,
  toggleRlcsInteractiveSelected,
  onChangeRlcsPlatform,
  onChangeRlcsLocale,
  onChangeRlcsGeolocation
}) => {
  const inputDisabled = !analysisInfo?.enabled || disabled;

  const filterByAnalysisMode = ([, platform]: [string, string]) => {
    if (!rlcsParams.interactive) return true;
    return PLATFORMS_SUPPORTED_BY_INTERACTIVE_ANALYSIS.includes(platform);
  };

  const shouldShowTooltip =
    inputDisabled && !!analysisInfo?.additional_information;

  const helpText =
    analysisInfo?.additional_information ??
    (!analysisInfo?.last_completed_on
      ? analysisInfo?.display_status
      : undefined);

  return (
    <ReanalyzeCardContainer
      classNameInnerContainer={clsx("p-0 border-1", "mb-10")}
      disabled={inputDisabled}
      selected={rlcsSelected}
      hoverable={false}
      layout={LAYOUT_TYPE.NONE}
      tooltipMessage={
        shouldShowTooltip ? analysisInfo?.additional_information : undefined
      }
    >
      <div className={"w-100"}>
        <div
          className={clsx(
            "py-15 px-20 cursor-pointer",
            cardStyles.card_hoverable
          )}
          onClick={() => {
            if (inputDisabled) {
              return;
            }
            toggleRlcsSelected();
          }}
        >
          <ReanalyzeFormInput
            name="rl_dynamic_analysis"
            label="Dynamic Analysis"
            labelIcon="dynamic-analysis"
            labelIconSize={24}
            help={"RL Cloud Sandbox"}
            helpImportant={shouldShowTooltip ? undefined : helpText}
            timestamp={analysisInfo?.last_completed_on}
            disabled={inputDisabled}
            layout={LAYOUT_TYPE.ROW}
            checked={rlcsSelected}
          />
        </div>
        {rlcsSelected && (
          <div className={"d-flex flex-column border-top border-primary"}>
            {settings.FEATURE_RL_INTERACTIVE_ANALYSIS && (
              <div className={"d-flex align-items-center gap-10 p-15"}>
                <Tooltip
                  content="Interactive analysis is available only if one file is selected or for URL analysis of a single file."
                  disabled={!interactiveAnalysisDisabled}
                >
                  <Switch
                    name={"rl_interactive_analysis_switch"}
                    onChange={() => {
                      if (interactiveAnalysisDisabled || inputDisabled) {
                        return;
                      }

                      const updatedPlatform = !rlcsParams.interactive
                        ? enums.platforms.WINDOWS_11
                        : rlcsParams.platform;

                      toggleRlcsInteractiveSelected(updatedPlatform);
                    }}
                    checked={rlcsParams.interactive}
                    label={"Interactive Analysis"}
                    disabled={interactiveAnalysisDisabled || inputDisabled}
                    data-testid={"interactive_analysis_switch"}
                  />
                </Tooltip>
              </div>
            )}
            <div className={"d-flex flex-row border-top"}>
              <ReanalyzeDropdown<string>
                filterValue={rlcsParams.platform}
                fieldName={"PLATFORM"}
                className={"flex-grow-1 w-100"}
                options={Object.entries(enums.platforms)
                  .filter(filterByAnalysisMode)
                  .map(([key, value]) => getMenuItemForPlatform(key, value))}
                filterTitle={"platform"}
                menuStyles={DEFAULT_MENU_STYLES}
                onFilterChange={onChangeRlcsPlatform}
                disabled={inputDisabled}
              />
              <ReanalyzeDropdown<string>
                filterValue={
                  rlcsParams.interactive ? undefined : rlcsParams.locale
                }
                filterTitle={"locale"}
                className={
                  "flex-grow-1 w-100 border-end border-start border-color-gray-84"
                }
                options={Object.values(enums.locales).map(value => ({
                  label: value as string,
                  value: value as string
                }))}
                fieldName={"locale"}
                menuStyles={DEFAULT_MENU_STYLES}
                onFilterChange={onChangeRlcsLocale}
                disabled={
                  !isLocaleAvailableForPlatform(rlcsParams.platform) ||
                  inputDisabled ||
                  rlcsParams.interactive
                }
              />
              <ReanalyzeDropdown<string>
                filterValue={
                  rlcsParams.interactive ? undefined : rlcsParams.geolocation
                }
                filterTitle={"geolocation"}
                className={"flex-grow-1 w-100"}
                options={Object.values(enums.geolocations).map(value => ({
                  label: GEOLOCATION_TO_LABEL[value as string],
                  value: value as string
                }))}
                fieldName={"geolocation"}
                menuStyles={DEFAULT_MENU_STYLES}
                onFilterChange={onChangeRlcsGeolocation}
                formatLabel={menuItem => (
                  <GeolocationMenuItem value={menuItem?.value} />
                )}
                disabled={inputDisabled || rlcsParams.interactive}
              />
            </div>
          </div>
        )}
      </div>
    </ReanalyzeCardContainer>
  );
};

export default RlcsConfigurationInput;
